/**
 * ProjectShowcase - Core Reusable Project Showcase Component
 * 
 * A theme-agnostic project showcase component for displaying property development projects.
 * This is a SCORABLE component - reusable across all themes.
 * 
 * Features:
 * - Full-screen immersive hero layout
 * - Interactive media type switching
 * - Unit availability tracking
 * - Brand customization support
 * - Responsive design
 */

'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import type { MediaLink } from '@/lib/api/clients/properties';

export interface ProjectShowcaseProps {
  project: PropertyDevelopmentProject;
  media?: MediaLink[];
  brandLogo?: string;
  brandColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  messages: {
    availableUnits: string;
    soldUnits: string;
    totalUnits: string;
    viewFloorPlans: string;
    viewSitePlan: string;
    view3DModel: string;
    viewGallery: string;
    enquireNow: string;
    projectStatus: string;
    completionDate: string;
    startingFrom: string;
    developedBy: string;
  };
  locale?: string;
  className?: string;
  // Render props for customization
  renderMediaButton?: (props: {
    isActive: boolean;
    onClick: () => void;
    label: string;
    colors: { primary: string; accent: string };
  }) => React.ReactNode;
  renderUnitCard?: (props: {
    project: PropertyDevelopmentProject;
    messages: ProjectShowcaseProps['messages'];
    colors: { primary: string; accent: string };
    formatDate: (date: string | null) => string | null;
  }) => React.ReactNode;
}

type MediaType = 'gallery' | 'floorplan' | 'siteplan' | '3d';

export function ProjectShowcase({
  project,
  media = [],
  brandLogo,
  brandColors,
  messages,
  locale = 'en',
  className = '',
  renderMediaButton,
  renderUnitCard,
}: ProjectShowcaseProps) {
  const [activeMedia, setActiveMedia] = useState<MediaType>('gallery');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Get color scheme - use brand colors if provided, otherwise defaults
  const colors = {
    primary: brandColors?.primary || '#000000',
    secondary: brandColors?.secondary || '#1a1a1a',
    accent: brandColors?.accent || '#666666',
  };

  // Filter media by type
  const galleryImages = media.filter(m => 
    m.usageContext === 'GALLERY' || m.usageContext === 'PRIMARY_HERO' || m.usageContext === 'AERIAL'
  );
  const floorPlans = media.filter(m => m.usageContext === 'LAYOUT_PLAN');
  const sitePlans = media.filter(m => m.contextualTitle?.toLowerCase().includes('site'));
  const model3D = media.filter(m => m.usageContext === 'VIRTUAL_TOUR_360');

  // Get current media based on active type
  const getCurrentMedia = () => {
    switch (activeMedia) {
      case 'floorplan':
        return floorPlans;
      case 'siteplan':
        return sitePlans;
      case '3d':
        return model3D;
      default:
        return galleryImages;
    }
  };

  const currentMedia = getCurrentMedia();
  const currentImage = currentMedia[currentImageIndex];

  // Calculate availability percentage
  const availabilityPercentage = project.totalUnits && project.soldUnits
    ? ((project.totalUnits - project.soldUnits) / project.totalUnits) * 100
    : 0;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  // Default media button renderer
  const defaultRenderMediaButton = (props: {
    isActive: boolean;
    onClick: () => void;
    label: string;
    colors: { primary: string; accent: string };
  }) => (
    <button
      onClick={props.onClick}
      className={`rounded-xl px-6 py-3 text-sm font-medium transition-all ${
        props.isActive
          ? 'bg-white text-gray-900 shadow-xl'
          : 'bg-white/20 text-white backdrop-blur-md hover:bg-white/30'
      }`}
      style={props.isActive ? { backgroundColor: props.colors.accent, color: '#fff' } : {}}
    >
      {props.label}
    </button>
  );

  // Default unit card renderer
  const defaultRenderUnitCard = (props: {
    project: PropertyDevelopmentProject;
    messages: ProjectShowcaseProps['messages'];
    colors: { primary: string; accent: string };
    formatDate: (date: string | null) => string | null;
  }) => (
    <div className="rounded-2xl bg-white p-8 shadow-2xl">
      {/* Unit Availability */}
      <div className="mb-6">
        <h3 
          className="mb-4 text-2xl font-bold"
          style={{ color: props.colors.primary }}
        >
          {props.messages.availableUnits}
        </h3>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="mb-2 flex justify-between text-sm font-medium text-gray-600">
            <span>{props.project.availableUnits || 0} Available</span>
            <span>{props.project.soldUnits || 0} Sold</span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${availabilityPercentage}%`,
                backgroundColor: props.colors.accent,
              }}
            />
          </div>
          <div className="mt-2 text-center text-xs font-medium text-gray-500">
            {Math.round(availabilityPercentage)}% {props.messages.availableUnits}
          </div>
        </div>

        {/* Unit Stats */}
        <div className="grid grid-cols-3 gap-4 border-t border-gray-200 pt-4">
          <div className="text-center">
            <div 
              className="text-3xl font-bold"
              style={{ color: props.colors.accent }}
            >
              {props.project.totalUnits || 0}
            </div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {props.messages.totalUnits}
            </div>
          </div>
          <div className="text-center">
            <div 
              className="text-3xl font-bold"
              style={{ color: props.colors.accent }}
            >
              {props.project.availableUnits || 0}
            </div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Available
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-400">
              {props.project.soldUnits || 0}
            </div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Sold
            </div>
          </div>
        </div>
      </div>

      {/* Unit Mix Summary */}
      {props.project.unitMixSummary && (
        <div className="mb-6 rounded-lg bg-gray-50 p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
            Unit Mix
          </div>
          <div className="text-sm text-gray-700">
            {props.project.unitMixSummary}
          </div>
        </div>
      )}

      {/* CTA Button */}
      <button
        className="w-full rounded-xl py-4 text-center text-base font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]"
        style={{ backgroundColor: props.colors.primary }}
      >
        {props.messages.enquireNow}
      </button>

      {/* Developer Info */}
      {props.project.developerOrganisationId && (
        <div className="mt-4 text-center text-xs text-gray-500">
          {props.messages.developedBy}
        </div>
      )}
    </div>
  );

  return (
    <div className={`relative w-full ${className}`}>
      {/* Main Hero Section - Full Screen Immersive */}
      <div className="relative h-screen min-h-[800px] w-full overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          {currentImage?.media?.url ? (
            <Image
              src={currentImage.media.url}
              alt={currentImage.contextualTitle || project.name}
              fill
              className="object-cover transition-all duration-700 ease-out"
              priority
              sizes="100vw"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
          )}
          
          {/* Gradient Overlay - Dynamic based on brand colors */}
          <div 
            className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"
            style={{
              background: `linear-gradient(to top, ${colors.secondary}f0 0%, ${colors.secondary}66 50%, transparent 100%)`,
            }}
          />
        </div>

        {/* Project Branding - Top Left */}
        {brandLogo && (
          <div className="absolute left-8 top-8 z-20 lg:left-16 lg:top-16">
            <div className="rounded-2xl bg-white/95 p-6 shadow-2xl backdrop-blur-sm">
              <Image
                src={brandLogo}
                alt={`${project.name} logo`}
                width={180}
                height={60}
                className="h-auto w-32 object-contain lg:w-44"
              />
            </div>
          </div>
        )}

        {/* Media Type Switcher - Top Right */}
        <div className="absolute right-8 top-8 z-20 flex flex-col gap-3 lg:right-16 lg:top-16">
          {galleryImages.length > 0 && (renderMediaButton || defaultRenderMediaButton)({
            isActive: activeMedia === 'gallery',
            onClick: () => {
              setActiveMedia('gallery');
              setCurrentImageIndex(0);
            },
            label: messages.viewGallery,
            colors: { primary: colors.primary, accent: colors.accent },
          })}
          {floorPlans.length > 0 && (renderMediaButton || defaultRenderMediaButton)({
            isActive: activeMedia === 'floorplan',
            onClick: () => {
              setActiveMedia('floorplan');
              setCurrentImageIndex(0);
            },
            label: messages.viewFloorPlans,
            colors: { primary: colors.primary, accent: colors.accent },
          })}
          {sitePlans.length > 0 && (renderMediaButton || defaultRenderMediaButton)({
            isActive: activeMedia === 'siteplan',
            onClick: () => {
              setActiveMedia('siteplan');
              setCurrentImageIndex(0);
            },
            label: messages.viewSitePlan,
            colors: { primary: colors.primary, accent: colors.accent },
          })}
          {model3D.length > 0 && (renderMediaButton || defaultRenderMediaButton)({
            isActive: activeMedia === '3d',
            onClick: () => {
              setActiveMedia('3d');
              setCurrentImageIndex(0);
            },
            label: messages.view3DModel,
            colors: { primary: colors.primary, accent: colors.accent },
          })}
        </div>

        {/* Main Content - Bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-10 px-8 pb-16 lg:px-16 lg:pb-20">
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
              {/* Left: Project Information */}
              <div className="lg:col-span-7">
                {/* Project Type Badge */}
                {project.projectType && (
                  <div className="mb-4 inline-block">
                    <span 
                      className="inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white"
                      style={{ backgroundColor: colors.accent }}
                    >
                      {project.projectType.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}

                {/* Project Name */}
                <h1 
                  className="mb-6 text-5xl font-bold leading-tight text-white lg:text-7xl"
                >
                  {project.name}
                </h1>

                {/* Project Description */}
                {project.description && (
                  <p className="mb-8 max-w-2xl text-lg leading-relaxed text-white/90 lg:text-xl">
                    {project.description}
                  </p>
                )}

                {/* Key Info Pills */}
                <div className="flex flex-wrap gap-4">
                  {project.status && (
                    <div className="rounded-lg bg-white/10 px-4 py-2 backdrop-blur-md">
                      <div className="text-xs font-medium uppercase tracking-wide text-white/70">
                        {messages.projectStatus}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-white">
                        {project.status.replace(/_/g, ' ')}
                      </div>
                    </div>
                  )}

                  {project.estimatedCompletionDate && (
                    <div className="rounded-lg bg-white/10 px-4 py-2 backdrop-blur-md">
                      <div className="text-xs font-medium uppercase tracking-wide text-white/70">
                        {messages.completionDate}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-white">
                        {formatDate(project.estimatedCompletionDate)}
                      </div>
                    </div>
                  )}

                  {project.displayPriceText && (
                    <div className="rounded-lg bg-white/10 px-4 py-2 backdrop-blur-md">
                      <div className="text-xs font-medium uppercase tracking-wide text-white/70">
                        {messages.startingFrom}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-white">
                        {project.displayPriceText}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Unit Availability Card */}
              <div className="lg:col-span-5">
                {(renderUnitCard || defaultRenderUnitCard)({
                  project,
                  messages,
                  colors: { primary: colors.primary, accent: colors.accent },
                  formatDate,
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Image Navigation Dots */}
        {currentMedia.length > 1 && (
          <div className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 gap-2">
            {currentMedia.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'w-8 bg-white'
                    : 'w-2 bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`View image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

